import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input,
         OnChanges, OnInit, Output, SimpleChanges, ViewChild
       } from '@angular/core';

import { StageConstants } from 'core/constants';
import { ProductService } from 'core/product/product.service';
import { EventTrackingService } from 'core/tracking/event-tracking.service';
import { FontInjectorService
       } from '../../collage-designer/font-injector.service';
import { TextPlaceholder
       } from '../../collage-designer/stage/placeholder/text-placeholder';
import { Utils } from '../../core/utils';
import { TextApplianceResult, TextController, TextEditingResult,
       } from '../../text/text.controller';
import { TextEditingRefusalReason } from '../../text/text.controller.methods';
import { selectCompleteText, Text, TextAlignment, TextChunk, TextChunkKind,
         updateText
       } from '../../text/text.model';
import { ToastComponent } from '../toast/toast.component';
import { getRejectionMessage, RejectionOptions, RejectionReason
       } from './text-editing.messages';
import { TextSizeChooserComponent } from './text-size-chooser.component';

enum EditingAction {
  ChangeText,
  ChangeFont,
  ChangeFontSize,
  Unspecified,
}

interface ControlsVisibility
{
  alignment: boolean;
  font: boolean;
  color: boolean;
  fontSize: boolean;
}

function determineFeedbackMessage(
    action: EditingAction,
    result: TextEditingResult,
    options: RejectionOptions): string | undefined
{
  if ([undefined, TextEditingRefusalReason.None].indexOf(result.reason) !== -1)
  {
    // Nothing unusual happened.
    return undefined;
  }

  switch (action) {
    case EditingAction.ChangeText:
      if (result.type === TextApplianceResult.Success) {
        // The text had to be rescaled while entering characters.
        // But the scaling was successful.
        return undefined;
      }
      else {
        // The text is too long and could not be rescaled
        if (result.reason === TextEditingRefusalReason.TextTooHigh) {
          return getRejectionMessage(RejectionReason.TextTooHigh, options);
        }

        if (result.reason === TextEditingRefusalReason.TextTooWide) {
          return getRejectionMessage(RejectionReason.TextTooWide, options);
        }

        if (result.reason === TextEditingRefusalReason.CharacterNotSupported) {
          return getRejectionMessage(RejectionReason.InvalidCharacters);
        }

        return getRejectionMessage(RejectionReason.Unspecified);
      }

    case EditingAction.ChangeFontSize:
      // the selected font size was too large or too small
      return getRejectionMessage(
        result.reason === TextEditingRefusalReason.MinFontSizeUndershot
          ? RejectionReason.FontSizeTooSmall
          : RejectionReason.FontSizeTooLarge);

    case EditingAction.ChangeFont:
      if (result.type === TextApplianceResult.Success) {
        // The new font was rescaled to fit
        return undefined;
      }
      else {
        if (result.reason === TextEditingRefusalReason.CharacterNotSupported) {
          return getRejectionMessage(RejectionReason.InvalidCharacters);
        } else {
          return getRejectionMessage(RejectionReason.FontFamilyTooLarge);
        }
      }

    default:
      return getRejectionMessage(RejectionReason.Unspecified);
  }
}

type OptionalTextSizeChooserComponent = TextSizeChooserComponent | undefined;

@Component({
  selector: 'text-editor',
  templateUrl: './text-editing.component.html',
  styleUrls: [ './text-editing.component.scss' ]
})
export class TextEditingComponent implements OnChanges, OnInit
{
  @Input('placeholder') public placeholder: TextPlaceholder;
  @Output() public editingResponse: EventEmitter<string> = new EventEmitter();
  public textModel: Text | undefined = undefined;
  public initialTextModel: Text | undefined = undefined;
  public fontName: string = '';
  public minFontSize: number = 0;
  public isTextEmpty: boolean = true;

  public controls: ControlsVisibility = {
    alignment: false,
    font: false,
    color: false,
    fontSize: false,
  };

  private controller: TextController | undefined = undefined;
  @ViewChild('sizeChooser') private sizeChooser:
      OptionalTextSizeChooserComponent = undefined;
  @ViewChild('editor') private editor: ElementRef;
  @ViewChild('toast') private toast: ToastComponent;

  public constructor(
    private fontInjectorService: FontInjectorService,
    private changeDetector: ChangeDetectorRef,
    private eventTrackingService: EventTrackingService,
    private productService: ProductService)
  {}

  public async ngOnInit(): Promise<void>
  {
    if (!this.textModel) {
      this.textModel = await this.placeholder.getTextModel();
    }

    if (!Utils.isSmallDevice() && this.placeholder.isUnchanged()) {
      this.textModel = selectCompleteText(this.textModel);
    }

    this.initialTextModel = Utils.deepCopy(this.textModel);

    this.minFontSize = Math.round(this.placeholder.toDisplayFontSize(
        this.placeholder.getMinFontSize()));
  }

  public async ngOnChanges(changes: SimpleChanges): Promise<void>
  {
    if ('placeholder' in changes) {
      const placeholder = changes['placeholder'].currentValue;
      this.controller = placeholder.zoomModeTextController;
      this.setTextModel(
          placeholder ? (await placeholder.getTextModel()) : undefined);
      this.setControlsVisibility(placeholder);

      this.minFontSize =
        Math.round(placeholder.toDisplayFontSize(placeholder.getMinFontSize()));
    }
  }

  public onTextChanged(text: Array<TextChunk>): void
  {
    if (! this.textModel) {
      return;
    }

    this.evaluateControllerResult(
      EditingAction.ChangeText,
      this.controller.setText(
        this.textModel,
        text,
        {minFontSize: this.minFontSize}));

    this.reactToEasterEggs(text);
  }

  public onAlignmentChanged(alignment: TextAlignment): void
  {
    this.evaluateControllerResult(
      EditingAction.Unspecified,
      this.controller.setHorizontalAlignment(this.textModel, alignment));
  }

  public onFontNameChanged(fontName: string): void
  {
    this.changeDetector.detach();

    this.fontName = '';
    this.changeDetector.detectChanges();

    this.evaluateControllerResult(
      EditingAction.ChangeFont,
      this.controller.setFont(
        this.textModel,
        this.fontInjectorService.getFont(fontName),
        {minFontSize: this.minFontSize}));
    this.changeDetector.detectChanges();

    this.changeDetector.reattach();
  }

  public onColorChanged(color: string): void
  {
    this.evaluateControllerResult(
      EditingAction.Unspecified,
      this.controller.setColor(this.textModel, color));
  }

  public onFontSizeChanged(size: number): void
  {
    this.evaluateControllerResult(
      EditingAction.ChangeFontSize,
      this.controller.setFontSize(
        this.textModel,
        size,
        {minFontSize: this.minFontSize}));
  }

  private evaluateControllerResult(
    action: EditingAction, result: TextEditingResult): void
  {
    this.setTextModel({...result.model});
    if (this.sizeChooser) {
      this.sizeChooser.resetInputField();
    }

    const feedbackMessage = determineFeedbackMessage(
        action, result, {
          flexible: this.placeholder.isFlexible(),
          zoomMode: this.placeholder.zoomMode
        });

    if (feedbackMessage !== undefined) {
      this.toast.show(feedbackMessage);
    }

    if (!Utils.isSmallDevice()) {
      this.editor.nativeElement.focus();
    }
  }

  private setTextModel(model: Text | undefined): void
  {
    if (! model) {
      this.textModel = undefined;
      return;
    }

    this.textModel = model;

    if (this.textModel.fontDescription) {
      this.fontName = this.textModel.fontDescription.name;
    }

    this.isTextEmpty = !this.textModel.text.some(
        el => el.kind !== TextChunkKind.Control);
  }

  public async onApplyClicked(): Promise<void>
  {
    const trimmed = chunks =>
      chunks.map(c => c.content ? c.content.trim() : '').join();
    const pel = this.placeholder.element.node;
    if (this.placeholder.isUnchanged() &&
        pel.hasAttribute(StageConstants.ATTRIBUTE_IS_VOLATILE_IF_UNCHANGED) &&
        pel.getAttribute(
            StageConstants.ATTRIBUTE_IS_VOLATILE_IF_UNCHANGED) === 'true' &&
        trimmed(this.textModel.text) === trimmed(this.initialTextModel.text)) {
      return this.onCancelClicked();
    }

    const text = this.textModel.text.filter(
        el => el.kind !== TextChunkKind.Control);

    this.textModel = updateText(this.textModel, text);
    this.placeholder.setTextModel(this.textModel);
    await this.placeholder.commitContent();
    this.editingResponse.emit('confirm');
    this.eventTrackingService.track({
      category: this.productService.getCurrentProduct().id,
      action: 'edit_text',
      label: 'apply'
    });
  }

  public onCancelClicked(): void
  {
    this.editingResponse.emit('cancel');
    this.eventTrackingService.track({
      category: this.productService.getCurrentProduct().id,
      action: 'edit_text',
      label: 'cancel'
    });
  }

  private reactToEasterEggs(text: Array<TextChunk>): void
  {
    const plaintext = text.reduce((txt, ch) => txt += ch.content || '', '');

    switch (plaintext) {
      case 'do a barrel roll!':
        this.editingResponse.emit('barrellRoll');
        break;

      default:
        break;
    }
  }

  private setControlsVisibility(placeholder: TextPlaceholder): void
  {
    this.controls = {
      alignment: placeholder.alignmentChangeable,
      font: placeholder.fontChangeable,
      color: placeholder.colorChangeable,
      fontSize: placeholder.fontSizeChangeable,
    };
  }
}



// WEBPACK FOOTER //
// ./src/app/controls/text-editing/text-editing.component.ts